import {FC, useEffect, useState, useRef} from "react";
import {App2NotificationProviderInner} from "./App2NotificationContext";
import {Capacitor} from "@capacitor/core";
import {
	ActionPerformed,
	PushNotificationSchema,
	PushNotifications,
	Token
} from "@capacitor/push-notifications";
import {useRegisterNotificationToken} from "@/app2/shared/data";
import {mergePushNotificationCustomPayload} from "@/utils/openAppStoreFromUpdatePushNotification";

interface IProps {
	children: React.ReactNode;
}

/**
 * App2-only notification provider. Used exclusively in the app2 tree (Main.tsx App2).
 * Provides notify() via reimplemented app2 notification context and registers FCM token with improve-api.
 * Legacy must not import this file; legacy uses NotificationProvider in RootLayout.
 */
export const App2NotificationProvider: FC<IProps> = ({children}) => {
	const [token, setToken] = useState<string>();
	const {mutate: registerToken} = useRegisterNotificationToken();
	const registered = useRef(false);
	const platform = Capacitor.getPlatform();

	useEffect(() => {
		if (platform === "web") return;
		PushNotifications.requestPermissions().then((permission) => {
			if (permission.receive === "granted") {
				PushNotifications.register();
			}
		});

		PushNotifications.addListener("registration", (FCMToken: Token) => {
			setToken(FCMToken.value);
		});

		PushNotifications.addListener("registrationError", (error: unknown) => {
			console.error("App2 push registration error:", error);
		});

		PushNotifications.addListener(
			"pushNotificationReceived",
			(_notification: PushNotificationSchema) => {}
		);

		PushNotifications.addListener(
			"pushNotificationActionPerformed",
			(notification: ActionPerformed) => {
				const merged = mergePushNotificationCustomPayload(notification.notification);
				if (merged?.detailsId) {
					// navigate to detail if needed
				}
			}
		);
	}, []);

	useEffect(() => {
		if (!token) return;
		if (registered.current) return;
		registered.current = true;
		registerToken(token, {
			onError: () => {
				registered.current = false;
			}
		});
	}, [token, registerToken]);

	return <App2NotificationProviderInner>{children}</App2NotificationProviderInner>;
};
